import { useState, useEffect } from 'react';
import { updateMetaTags, addJsonLd } from '../../utils/seo';
import { supabase, type ArticleCache } from '../../lib/supabase';

interface BlogArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  slug: string;
}

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tous');
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['Tous']);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('articles_cache')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des articles:', error);
        return;
      }

      if (data && data.length > 0) {
        // Transformer les données Supabase en format BlogArticle
        const transformedArticles: BlogArticle[] = data.map((article: ArticleCache) => {
          // Extraire l'extrait du contenu HTML (premiers 200 caractères)
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = article.content || '';
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          const excerpt = textContent.substring(0, 200) + '...';

          // Calculer le temps de lecture (environ 200 mots par minute)
          const wordCount = textContent.split(/\s+/).length;
          const readTime = Math.max(1, Math.ceil(wordCount / 200));

          return {
            id: article.id,
            slug: article.slug,
            title: article.title,
            excerpt: excerpt,
            category: 'Article',
            date: article.published_at 
              ? new Date(article.published_at).toLocaleDateString('fr-FR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })
              : '',
            readTime: `${readTime} min`,
            image: article.image_url || 'https://public.readdy.ai/ai/img_res/0cf39c114a1760eb2c9af26c84d818e5.jpg',
            author: 'Vist Magazine'
          };
        });

        setArticles(transformedArticles);

        // Extraire les catégories uniques
        const uniqueCategories = ['Tous', ...new Set(transformedArticles.map(a => a.category))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // SEO Meta Tags
    updateMetaTags({
      title: 'Blog Vist Magazine - Analyses et décryptages des tendances contemporaines',
      description: 'Analyses, décryptages et réflexions sur les tendances qui façonnent notre époque. Un regard éditorial sur la culture, la société et les usages digitaux.',
      keywords: 'blog magazine, analyses culturelles, tendances digitales, société contemporaine, lifestyle moderne, médias numériques',
      canonical: '/blog',
      ogType: 'blog'
    });

    // Schema.org JSON-LD for Blog
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Blog Vist Magazine",
      "url": `${baseUrl}/blog`,
      "description": "Analyses, décryptages et réflexions sur les tendances qui façonnent notre époque",
      "publisher": {
        "@type": "Organization",
        "name": "Vist Magazine",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png"
        }
      },
      "blogPost": articles.slice(0, 5).map(article => ({
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.excerpt,
        "datePublished": new Date(article.date).toISOString(),
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "image": article.image,
        "articleSection": article.category
      }))
    });
  }, [articles]);
  
  const filteredArticles = selectedCategory === 'Tous' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
              <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
              <a href="/blog" className="text-sm text-gray-900 font-medium whitespace-nowrap cursor-pointer">Blog</a>
              <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
              <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
              <a href="/blog" className="block py-2 text-sm text-gray-900 font-medium">Blog</a>
              <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
              <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
              Blog Vist
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Analyses, décryptages et réflexions sur les tendances qui façonnent notre époque. 
              Un regard éditorial sur la culture, la société et les usages digitaux.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Categories */}
      <section className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
              <p className="text-gray-500 text-base md:text-lg mt-4">Chargement des articles...</p>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      {!loading && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredArticles.map((article) => (
                  <article 
                    key={article.id} 
                    className="group cursor-pointer"
                    onClick={() => window.REACT_APP_NAVIGATE(`/blog/${article.slug}`)}
                  >
                    <div className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-gray-200 transition-all hover:shadow-lg">
                      {/* Image */}
                      <div className="relative w-full h-48 md:h-56 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/95 backdrop-blur-sm text-xs font-medium text-gray-900 rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5 md:p-6">
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                          <span>{article.date}</span>
                          <span>•</span>
                          <span>{article.readTime} de lecture</span>
                        </div>

                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-amber-600 transition-colors">
                          {article.title}
                        </h3>

                        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2 md:line-clamp-3">
                          {article.excerpt}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-sm text-gray-700 font-medium">{article.author}</span>
                          <span className="text-sm text-amber-600 font-medium group-hover:translate-x-1 transition-transform">
                            Lire l'article →
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-500 text-base md:text-lg mb-4">Aucun article disponible pour le moment.</p>
                <p className="text-sm text-gray-400">Les articles seront synchronisés automatiquement depuis le CMS toutes les 15 minutes.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-rose-50 py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Restez informé des nouvelles publications
          </h2>
          <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
            Recevez nos derniers articles directement dans votre boîte mail.
          </p>
          <a 
            href="/contact" 
            className="inline-block px-6 md:px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
          >
            S'abonner à la newsletter
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Vist</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Magazine digital indépendant consacré à l'analyse des tendances culturelles, sociétales et digitales.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Accueil</a></li>
                <li><a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Magazine</a></li>
                <li><a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">À propos</a></li>
                <li><a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2">
                <li><a href="/mentions-legales" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Mentions légales</a></li>
                <li><a href="/confidentialite" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Confidentialité</a></li>
                <li><a href="/conditions" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Conditions</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600 text-center md:text-left">
                © {new Date().getFullYear()} Vist Magazine. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
